import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import "../../Assets/scss/components/IPAddressSection.scss";
import TrustedIPManager from "../../Components/TrustedIPManager/TrustedIPManager";
import { useTranslation } from "react-i18next";
import { LOADING, SET_MESSAGE } from "../../Actions/Types";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useSelector, useDispatch } from "react-redux";
import DisableIPConfirmModal from "./DisableIPAddress";
import { toggleUserIPStatus, getUserIPAddress } from "../../Api/IPaddressAPI/index";
import { getUserDetailsApi } from "../../Api/UserAction/LoginActionApi";

interface IPAddressSectionProps {
  userId: string;
}

const IPAddressSection: React.FC<IPAddressSectionProps> = ({ userId }) => {
  const [ipCheckEnabled, setIpCheckEnabled] = useState<number>(); // stores IP restriction status (0 or 1)
  const [enable, setEnable] = useState<boolean>(false); // toggles IP manager UI
  const [showConfirmModal, setShowConfirmModal] = useState(false); // modal visibility for disabling IP
  const { accessToken } = useSelector((store: StoreProptypes) => store);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Fetch user details to check IP restriction status
  const getUser = (): void => {
    if (accessToken) {
      dispatch({ type: LOADING, payload: true }); // Start loader
      getUserDetailsApi(accessToken)
        .then((res) => {
          if (res?.Data) {
            setIpCheckEnabled(res?.Data?.ip_enabled);
          }
        })
        .catch((err) => {
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: `${err}`,
              toast_type: "danger",
            },
          });
        })
        .finally(() => {
          dispatch({ type: LOADING, payload: false }); // End loader
        });
    }
  };

  useEffect(() => {
    getUser();
  }, [accessToken]);

  // Toggle the IP verification feature on/off
  const handleToggleIPRestriction = async (checked: boolean) => {
    if (!accessToken) return;

    dispatch({ type: LOADING, payload: true }); // Start loader
    try {
      const res = await toggleUserIPStatus(accessToken, { status: checked });

      if (res?.message) {
        getUser(); // Refresh status after successful toggle
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("IP restriction updated successfully"),
            toast_type: "success",
          },
        });
      }
      setShowConfirmModal(false)
    } catch (error) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Error updating IP restriction"),
          toast_type: "danger",
        },
      });
    } finally {
      dispatch({ type: LOADING, payload: false }); // End loader
    }
  };

 // Show IP manager when enabling for first time or re-enabling manually
const handleToggle = async () => {
  if (ipCheckEnabled === 0) {
    setEnable(true);

    // ✅ Check if accessToken exists before calling the API
    if (accessToken) {
      try {
        const storedIPs = await getUserIPAddress(accessToken);
        if (storedIPs?.data?.length > 0) {
          await handleToggleIPRestriction(true);
        }
      } catch (err) {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("Failed to verify IP status."),
            toast_type: "danger",
          },
        });
      }
    }
  }
};


  return (
    <>
      {enable === false ? (
        <div className="ip-verification-box">
          <div className="ip-verification-header">
            <h5 className="mb-0">{t("IP Address Verification")}</h5>
          </div>
          <div className="ip-verification-body">
            <div>
            <p style={{ height: "51px"}}>
              {t(
                "Increase your account security by enabling IP address verification. Only trusted IPs will be allowed for login."
              )}
            </p>
            </div>

            <div className="d-flex gap-3 flex-wrap">
              {ipCheckEnabled === 1 ? (
                <div className="d-flex gap-3 flex-wrap align-items-center">
                  <Button
                    variant="primary"
                    className="add-btn"
                    onClick={() => setEnable(true)}
                  >
                    {t("Add IP")}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    {t("Disable IP Verification")}
                  </Button>
                </div>
              ) : (
                <Button variant="primary" onClick={handleToggle}>
                  {t("enable IP verification")}
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Show IP Manager Component
        <TrustedIPManager
          handleToggleIPRestriction={handleToggleIPRestriction}
          userId={userId}
          onBack={() => setEnable(false)} 
        />
      )}

      {/* Disable Confirmation Modal */}
      <DisableIPConfirmModal
        show={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => handleToggleIPRestriction(false)}
      />
    </>
  );
};

export default IPAddressSection;
